import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useApi } from '@fortem-monorepo/react-spa'
import { PnlAttributions } from '@fortem-monorepo/web-sdk'

import { toastApiError } from '@/lib/error-toast'

const useInvalidateState = (params: PnlAttributions.GetAttributionsStateParams) => {
  const queryClient = useQueryClient()

  return () => {
    if (params) {
      queryClient.invalidateQueries({ queryKey: ['pnl-attributions', 'state', params] })
      queryClient.invalidateQueries({ queryKey: ['pnl-attributions', 'request-history', params] })
    }
  }
}

export const useBeginValidatingMutation = (params: PnlAttributions.StartAttributionsParams) => {
  const api = useApi()
  const invalidateState = useInvalidateState(params)

  return useMutation({
    mutationFn: (params: PnlAttributions.StartAttributionsParams) => {
      return api.pnlAttributions.beginValidation(params)
    },
    onError(error, _params) {
      toastApiError(error, `Error starting attributions`)
    },
    onSettled: () => {
      invalidateState()
    },
  })
}

export const useRefreshAttributionsMutation = (params: PnlAttributions.StartAttributionsParams) => {
  const api = useApi()
  const invalidateState = useInvalidateState(params)

  return useMutation({
    mutationFn: (params: PnlAttributions.RefreshAttributionsParams) => {
      return api.pnlAttributions.refreshAttributions(params)
    },
    onError(error, _params) {
      toastApiError(error, `Error restarting attributions`)
    },
    onSettled: () => {
      invalidateState()
    },
  })
}

export const usePublishAttributionsMutation = (params: PnlAttributions.StartAttributionsParams) => {
  const api = useApi()
  const invalidateState = useInvalidateState(params)

  return useMutation({
    mutationFn: (params: PnlAttributions.PublishAttributionsParams) => {
      return api.pnlAttributions.publishAttributions(params)
    },
    onSuccess() {
      toast.success('Publish successful')
    },
    onError(error, _params) {
      toastApiError(error, `Error publishing attributions`)
    },
    onSettled: () => {
      invalidateState()
    },
  })
}

export const useAdjustPositionMutation = (params: PnlAttributions.StartAttributionsParams) => {
  const api = useApi()
  const invalidateState = useInvalidateState(params)

  return useMutation({
    mutationFn: (params: PnlAttributions.AdjustPositionParams) => {
      return api.pnlAttributions.position.adjust(params)
    },
    onSuccess() {
      toast.success('Adjustment created')
    },
    onError(error, _params) {
      toastApiError(error, `Error adjusting position`)
    },
    onSettled: () => {
      invalidateState()
    },
  })
}

export const useAddToPositionMutation = (params: PnlAttributions.StartAttributionsParams) => {
  const api = useApi()
  const invalidateState = useInvalidateState(params)

  return useMutation({
    mutationFn: (params: PnlAttributions.AddToPositionParams) => {
      return api.pnlAttributions.position.addComponent(params)
    },
    onSuccess() {
      toast.success('Component requested')
    },
    onError(error, _params) {
      toastApiError(error, `Error adding to position`)
    },
    onSettled: () => {
      invalidateState()
    },
  })
}

export const useAdjustTradeMutation = (params: PnlAttributions.StartAttributionsParams) => {
  const api = useApi()
  const invalidateState = useInvalidateState(params)

  return useMutation({
    mutationFn: (params: PnlAttributions.AdjustTradeParams) => {
      return api.pnlAttributions.trade.adjust(params)
    },
    onSuccess() {
      toast.success('Adjustment created')
    },
    onError(error, _params) {
      toastApiError(error, `Error adjusting trade`)
    },
    onSettled: () => {
      invalidateState()
    },
  })
}

export const useAddToTradeMutation = (params: PnlAttributions.StartAttributionsParams) => {
  const api = useApi()
  const invalidateState = useInvalidateState(params)

  return useMutation({
    mutationFn: (params: PnlAttributions.AddToTradeParams) => {
      return api.pnlAttributions.trade.addComponent(params)
    },
    onSuccess() {
      toast.success('Component requested')
    },
    onError(error, _params) {
      toastApiError(error, `Error adding to trade`)
    },
    onSettled: () => {
      invalidateState()
    },
  })
}

export const useApproveRequestMutation = (params: PnlAttributions.StartAttributionsParams) => {
  const api = useApi()
  const invalidateState = useInvalidateState(params)

  return useMutation({
    mutationFn: (params: PnlAttributions.ApproveRequestParams) => {
      return api.pnlAttributions.approveRequest(params)
    },
    onSuccess() {
      toast.success('Request approved')
    },
    onError(error, _params) {
      toastApiError(error, `Error approving the request`)
    },
    onSettled: () => {
      invalidateState()
    },
  })
}

export const useRejectRequestMutation = (params: PnlAttributions.StartAttributionsParams) => {
  const api = useApi()
  const invalidateState = useInvalidateState(params)

  return useMutation({
    mutationFn: (params: PnlAttributions.RejectRequestParams) => {
      return api.pnlAttributions.rejectRequest(params)
    },
    onSuccess() {
      toast.success('Request rejected')
    },
    onError(error, _params) {
      toastApiError(error, `Error rejecting the request`)
    },
    onSettled: () => {
      invalidateState()
    },
  })
}
